import { t } from "adapters/i18n";
import type { Dispatch } from "react-redux";
import type { RootState } from "redux/reducers";
import herdService from "services/herdService";
import { getErrorMessage } from "shared/errorUtils";
import {
  type LotActionsDispatchAction,
  exibirCarregando,
  getLotWeightProjection,
  listLotGoals,
  notifyError,
  notifySuccess,
} from "../actions";

export const fetchLotWeightGainProjection =
  (lotId: number) => async (dispatch: Dispatch<LotActionsDispatchAction>) => {
    try {
      dispatch(exibirCarregando(true));

      const projection = await herdService.getLotWeightGainProjection(lotId);
      dispatch(getLotWeightProjection(projection));
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("livestock:get-lot-projected-weight-error")
      );

      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

export const fetchLotGoals =
  (withDeleted?: boolean) =>
  async (
    dispatch: Dispatch<LotActionsDispatchAction>,
    getState: () => RootState
  ) => {
    try {
      dispatch(exibirCarregando(true));

      const { fazenda } = getState();
      const farmId = fazenda.FazendaSelecionada;

      if (farmId == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const goals = await herdService.listLotGoals(farmId, withDeleted);

      const sortedGoals = goals.data.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      );

      dispatch(listLotGoals(sortedGoals));
    } catch (error) {
      const message = getErrorMessage(error, t("errors:goal.list-goals"));
      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

export const createLotGoal =
  (payload: Omit<Parameters<typeof herdService.createGoal>[0], "farmId">) =>
  async (
    dispatch: Dispatch<LotActionsDispatchAction>,
    getState: () => RootState
  ) => {
    try {
      const { fazenda } = getState();
      const farmId = fazenda.FazendaSelecionada;

      if (farmId == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      await herdService.createGoal({ ...payload, farmId });
      dispatch(notifySuccess({ mensagem: t("success:goal.create-goal") }));
    } catch (error) {
      const message = getErrorMessage(error, t("errors:goal.create-goal"));
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const updateLotGoal =
  (id: number, payload: Parameters<typeof herdService.updateGoal>[1]) =>
  async (dispatch: Dispatch<LotActionsDispatchAction>) => {
    try {
      await herdService.updateGoal(id, payload);
      dispatch(notifySuccess({ mensagem: t("success:goal.update-goal") }));
    } catch (error) {
      const message = getErrorMessage(error, t("errors:goal.update-goal"));
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const deleteLotGoal =
  (id: number) => async (dispatch: Dispatch<LotActionsDispatchAction>) => {
    try {
      await herdService.deleteGoal(id);
      dispatch(notifySuccess({ mensagem: t("success:goal.delete-goal") }));
    } catch (error) {
      const message = getErrorMessage(error, t("errors:goal.delete-goal"));
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const reactivateLotGoal =
  (id: number) => async (dispatch: Dispatch<LotActionsDispatchAction>) => {
    try {
      await herdService.reactivateGoal(id);
      dispatch(notifySuccess({ mensagem: t("success:goal.reactivate-goal") }));
    } catch (error) {
      const message = getErrorMessage(error, t("errors:goal.reactivate-goal"));
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const updateLots =
  (payload: Parameters<typeof herdService.updateLots>[0]) =>
  async (dispatch: Dispatch<LotActionsDispatchAction>) => {
    try {
      dispatch(exibirCarregando(true));

      await herdService.updateLots(payload);

      dispatch(notifySuccess({ mensagem: t("livestock:update-lots-success") }));
    } catch (error) {
      const message = getErrorMessage(error, t("livestock:update-lots-error"));

      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };
